import { useMutation } from '@apollo/client';
import { Button, Col, Form, Input, InputRef, Row } from 'antd';
import { parsePhoneNumber } from 'libphonenumber-js';
import { useContext, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useWindowSize } from 'react-use';
import { DeviceInfo, User } from '../../__generated__/graphql';
import { AppContext } from '../../AppContext';
import LOGO from '../../assets/svg/vastu-logo.svg';
import {
  BROWSER_TYPES,
  LOCAL_STORAGE,
  REGEX,
  ROUTES,
} from '../../common/constants';
import { isMobileBrowser, removeItem, setItem } from '../../common/utils';
import { customizeRequiredMark } from '../../components/CustomRequiredMark';
import ResendOtp from '../../components/ResendOtpButton';
import useRouter from '../../hooks/useRouter';
import { AppContextType } from '../../types/appContext.type';
import '../auth/auth.less';
import AuthSlider from './AuthSlider';
import { LOGIN } from './graphql/mutations';

const VerifyOtp = () => {
  const [otp] = useState<string>('');
  const [verifyOtpForm] = Form.useForm();
  const { initializeAuth } = useContext(AppContext) as AppContextType;
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo>({
    os: '',
    osVersion: '',
    deviceModel: '',
    deviceId: '',
    appVersion: '',
  });
  const { navigate } = useRouter();
  const [getLoginInfo] = useMutation(LOGIN);
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState<boolean>(false);
  const otpInput = useRef<InputRef>(null);

  useEffect(() => {
    const platform = navigator.platform || '';
    const userAgent = navigator.userAgent || '';

    let os = platform.includes(BROWSER_TYPES.WINDOWS.key)
      ? BROWSER_TYPES.WINDOWS.value
      : platform.includes(BROWSER_TYPES.MAC.key)
        ? BROWSER_TYPES.MAC.value
        : platform.includes(BROWSER_TYPES.LINUX.key)
          ? BROWSER_TYPES.LINUX.value
          : '';

    let osVersion = '';
    let deviceModel = '';

    if (/Windows NT/.test(userAgent)) {
      os = BROWSER_TYPES.WINDOWS.value;
      const match = userAgent.match(/Windows NT (\d+\.\d+)/);
      if (match) {
        osVersion = match[1];
      }
    } else if (/Mac OS X/.test(userAgent)) {
      os = BROWSER_TYPES.MAC.value;
      const match = userAgent.match(/Mac OS X (\d+[._]\d+)/);
      if (match) {
        osVersion = match[1].replace('_', '.');
      }
    } else if (/Linux/.test(platform)) {
      os = BROWSER_TYPES.LINUX.value;
    }

    if (/Android/.test(userAgent)) {
      osVersion = userAgent.match(/Android (\d+\.\d+)/)?.[1] || osVersion;
      deviceModel = userAgent.match(/\((.*?)\)/)?.[1] || deviceModel;
    } else if (/iPhone|iPad|iPod/.test(userAgent)) {
      osVersion =
        userAgent.match(/OS (\d+_\d+)/)?.[1]?.replace('_', '.') || osVersion;
      deviceModel = userAgent.match(/\((.*?)\)/)?.[1] || deviceModel;
    }

    let appVersion = '';
    if (/Chrome/.test(userAgent)) {
      appVersion = userAgent.match(/Chrome\/(\d+\.\d+)/)?.[1] || appVersion;
    } else if (/Safari/.test(userAgent)) {
      appVersion = userAgent.match(/Version\/(\d+\.\d+)/)?.[1] || appVersion;
    } else if (/Firefox/.test(userAgent)) {
      appVersion = userAgent.match(/Firefox\/(\d+\.\d+)/)?.[1] || appVersion;
    }

    setDeviceInfo({
      os,
      osVersion,
      deviceModel,
      appVersion,
      deviceId: '',
    });
  }, []);

  useEffect(() => {
    if (otpInput.current) {
      otpInput.current.focus();
    }
  }, []);

  function successCallback(
    accessToken: string,
    userData: User,
    refreshToken: string,
  ) {
    initializeAuth(accessToken, userData, refreshToken);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleVerifyOtp = async (value: { otp: string }) => {
    const phone = searchParams.get('number');
    const phoneNumber = `+${phone?.replace(/\D/g, '')}`;
    const isNewNumber = parsePhoneNumber(phoneNumber);

    try {
      setLoading(true);
      // COMMENTING NEXT FEW LINES DUE TO ISSUE IN OTPLESS, WILL BE USED LATER
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const otpResponse: any = {};
      // const otpResponse: OTPLessResponse = await verify(
      //   isNewNumber?.nationalNumber,
      //   value?.otp,
      //   isNewNumber?.countryCallingCode,
      // );

      // if (otpResponse?.response?.errorMessage) {
      //   setLoading(false);
      //   message.error('Incorrect OTP. Please try again.');
      // } else {
      //   setLoading(false);
      // }
      // eslint-disable-next-line no-console
      // if (otpResponse?.response?.requestID || '') {
      setItem(
        LOCAL_STORAGE.OTP_LESS_TOKEN,
        otpResponse?.response?.requestID || '',
      );

      const response = await getLoginInfo({
        variables: {
          data: {
            deviceInfo,
            countryCode: isNewNumber?.countryCallingCode,
            phoneNo: isNewNumber?.nationalNumber,
            otpLessVerificationToken: otpResponse?.response?.requestID || '',
          },
        },
      });

      if (response?.data?.loginWeb?.accessToken) {
        const accessToken = response?.data?.loginWeb?.accessToken;
        const userData = response?.data?.loginWeb?.data;
        const refreshToken = response?.data?.loginWeb?.refreshToken;
        removeItem(LOCAL_STORAGE.LAST_PHONE_NUMBER);
        removeItem(LOCAL_STORAGE.OTP_ATTEMPTS);
        removeItem(LOCAL_STORAGE.OTP_START_TIME);
        removeItem(LOCAL_STORAGE.OTP_BLOCK_TIME);

        successCallback(
          accessToken as string,
          userData as User,
          refreshToken as string,
        );
        if (response?.data?.loginWeb?.data?.isRegistered) {
          navigate(ROUTES.MAIN);
        } else {
          navigate(ROUTES.REGISTER, { replace: true });
          window.history.pushState(null, '', ROUTES.REGISTER);
          window.onpopstate = () => navigate(ROUTES.MAIN);
        }
      }
    } catch {
      setLoading(false);
    }
  };

  const { width } = useWindowSize();

  return (
    <div className="otp-container">
      <Row>
        {!isMobileBrowser(width) && (
          <Col span={12} className="slider">
            <AuthSlider />
          </Col>
        )}
        {isMobileBrowser(width) && (
          <div className="logo-wrapper ">
            <img src={LOGO} alt="logo" className="main-logo"></img>
          </div>
        )}
        <Col span={12} className="otp-div">
          <Form
            form={verifyOtpForm}
            className="otp-form-div"
            layout="vertical"
            onFinish={handleVerifyOtp}
            requiredMark={customizeRequiredMark}
          >
            <div className="otp-form">
              <h2 className="title-lg semibold">Verify Your Number</h2>
              <h4 className="body-md">
                Enter the 6-digit OTP sent to &nbsp;
                <span className="body-md semibold">
                  +{searchParams.get('number')}
                </span>
              </h4>
              <h3
                className="change-number-text body-base medium"
                onClick={() => navigate(ROUTES.LOGIN)}
              >
                <span>Change Number</span>
              </h3>
              <Form.Item
                label={<span className="body-meta medium">Enter OTP</span>}
                name="otp"
                rules={[
                  {
                    required: true,
                    message: 'Please enter the code.',
                  },
                  {
                    pattern: REGEX.NUMBER,
                    message: 'OTP can only contain numbers!',
                  },
                ]}
              >
                <Input
                  ref={otpInput}
                  id="otp-input body-sm medium"
                  placeholder="000000"
                  value={otp}
                  maxLength={6}
                />
              </Form.Item>

              <Form.Item>
                <ResendOtp
                  verifyOtpForm={verifyOtpForm}
                  phoneNumber={searchParams?.get('number') || ''}
                />
              </Form.Item>
            </div>
            <div className="verify-btn-div">
              <Button
                type="primary"
                className="verify-button medium"
                htmlType="submit"
                loading={loading}
              >
                Continue
              </Button>
            </div>
          </Form>
        </Col>
      </Row>
    </div>
  );
};

export default VerifyOtp;
